import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedProps<V extends string = string> {
  options: readonly SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

interface IndicatorRect {
  readonly left: number;
  readonly width: number;
}

/** Single-select group with a positioned selected-state pill.
 *  Uses role=radiogroup + role=radio so screen readers announce
 *  the current choice — not just a row of buttons.
 *
 *  The pill (`<span class="vq-segmented__indicator">`) is a single
 *  absolutely-positioned element whose `left` + `width` are updated to
 *  match the active item. That pill is the only moving part; the options
 *  themselves do not scale or slide. ResizeObserver re-measures so the
 *  pill stays correct under font-size changes, density changes, or
 *  responsive label re-flow. */
export function Segmented<V extends string = string>(props: SegmentedProps<V>): ReactNode {
  const { options, value, onChange, label, size = 'md', className } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [indicatorReady, setIndicatorReady] = useState(false);

  useEffect(() => {
    if (!hasMeasured) return undefined;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        setIndicatorReady(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [hasMeasured]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const item = itemRefs.current.get(value);
    if (container === null || item === undefined) {
      setIndicator(null);
      return undefined;
    }
    const measure = (): void => {
      setIndicator({
        left: item.offsetLeft,
        width: item.offsetWidth,
      });
      setHasMeasured(true);
    };
    measure();
    scrollItemHorizontallyIntoView(container, item);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    ro.observe(item);
    return () => {
      ro.disconnect();
    };
  }, [value, options]);

  const classes = ['vq-segmented', className].filter(Boolean).join(' ');

  const indicatorStyle: CSSProperties =
    indicator !== null
      ? {
          left: `${String(indicator.left)}px`,
          width: `${String(indicator.width)}px`,
        }
      : { opacity: 0 };

  return (
    <div
      ref={containerRef}
      className={classes}
      data-component="Segmented"
      data-size={size}
      data-measured={hasMeasured ? 'true' : 'false'}
      data-indicator-ready={indicatorReady ? 'true' : 'false'}
      role="radiogroup"
      aria-label={label}
    >
      <span className="vq-segmented__indicator" aria-hidden="true" style={indicatorStyle} />
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={opt.disabled === true}
            className="vq-segmented__item"
            data-active={isActive ? 'true' : 'false'}
            ref={(el) => {
              if (el === null) itemRefs.current.delete(opt.value);
              else itemRefs.current.set(opt.value, el);
            }}
            onClick={() => {
              onChange(opt.value);
            }}
          >
            {opt.icon !== undefined ? (
              <span className="vq-segmented__icon" aria-hidden="true">
                {opt.icon}
              </span>
            ) : null}
            <span className="vq-segmented__label">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function scrollItemHorizontallyIntoView(
  container: HTMLElement,
  item: HTMLElement,
): void {
  const itemStart = item.offsetLeft;
  const itemEnd = itemStart + item.offsetWidth;
  const visibleStart = container.scrollLeft;
  const visibleEnd = visibleStart + container.clientWidth;

  if (itemStart < visibleStart) {
    container.scrollLeft = itemStart;
    return;
  }
  if (itemEnd > visibleEnd) {
    container.scrollLeft = itemEnd - container.clientWidth;
  }
}
